import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import InputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import useTemplates from "@/components/Templates/useTemplates";
import { zinc } from "@/ui/tokens";

/** The frame of the popovers the Dispatch page's composers open for their tools: the template list, the drop zone. */
export const toolPopoverPaperSx = {
  width: 360,
  maxHeight: "70vh",
  overflowY: "auto",
  bgcolor: zinc[800],
  border: `1px solid ${zinc[600]}`,
  color: "white",
  p: 1.5,
  mt: 0.5,
} as const;

/** How tall the list grows before it scrolls, so a workspace with many templates keeps the popover short. */
const LIST_MAX_HEIGHT = 240;

/**
 * The workspace's templates as a list to pick from: the dispatcher's own first, then everyone else's, each with its
 * name and the start of its text. A search box at the top narrows the list by name or text, and the list scrolls
 * past a fixed height rather than growing with the workspace. Picking one hands its text to the caller's composer.
 * Managing templates stays on its own page, linked at the foot. Shared by the row and thread composers and the
 * fleet message dialog.
 *
 * @param workspaceId - The workspace whose manage-templates page the foot links to.
 * @param onChoose - Receives the picked template's text.
 */
export default function TemplateChoices({
  workspaceId,
  onChoose,
}: {
  workspaceId: string;
  onChoose: (text: string) => void;
}) {
  const { templates } = useTemplates();
  const [query, setQuery] = useState("");
  const term = query.trim().toLowerCase();
  const choices = [...templates.myTemplates, ...templates.notMyTemplates].filter(
    (template) =>
      term === "" || template.name.toLowerCase().includes(term) || template.template.toLowerCase().includes(term),
  );
  const empty = term === "" ? "No templates yet." : "No templates match.";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: zinc[200] }}>Templates</Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          height: 30,
          px: 1,
          borderRadius: 1,
          bgcolor: zinc[700],
          fontSize: "0.8125rem",
        }}
      >
        <SearchIcon sx={{ fontSize: 16, color: zinc[400] }} />
        <InputBase
          autoFocus
          data-sb={TestIds.dispatch.composerTemplateSearch}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search templates"
          inputProps={{ "aria-label": "Search templates" }}
          sx={{ flex: 1, color: "white", fontSize: "inherit", "& input": { p: 0 } }}
        />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxHeight: LIST_MAX_HEIGHT, overflowY: "auto" }}>
        {choices.length === 0 ? (
          <Typography sx={{ fontSize: "0.8125rem", color: zinc[400], py: 1 }}>{empty}</Typography>
        ) : (
          choices.map((template) => (
            <ButtonBase
              key={template.id}
              data-sb={TestIds.dispatch.composerTemplate}
              onClick={() => onChoose(template.template)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 0.25,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                textAlign: "left",
                "&:hover": { bgcolor: zinc[700] },
              }}
            >
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "white" }}>{template.name}</Typography>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: zinc[400],
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {template.template}
              </Typography>
            </ButtonBase>
          ))
        )}
      </Box>
      <ButtonBase
        component={RouterLink}
        to={`/workspaces/${workspaceId}/manage-templates`}
        sx={{ alignSelf: "flex-start", mt: 0.5, fontSize: "0.75rem", color: zinc[300], "&:hover": { color: "white" } }}
      >
        Manage templates
      </ButtonBase>
    </Box>
  );
}
